import { computed, ref } from 'vue'
import { loginMember, logoutMember, withdrawMember, fetchMemberSession } from '@/api/member'
import { clearCartData } from '@/composables/useCart'
import { clearWishlistData } from '@/composables/useWishlist'
import { saveAutoLogin } from '@/constants/autoLogin'
import {
  clearMemberSession,
  loadMemberSession,
  saveMemberSession,
  type MemberSession,
} from '@/constants/memberSession'

const member = ref<MemberSession | null>(loadMemberSession())
const initialized = ref(false)
const initializing = ref(false)
/**
 * 새 탭(함우리몰)에서는 sessionStorage 가 비어 있다.
 * 헤더와 라우터가 동시에 세션을 물어보면, 먼저 호출한 쪽만 기다리고
 * 나머지는 아직 비어 있는 계정으로 로그인 화면을 연다.
 */
let pendingInit: Promise<void> | null = null

/** 장바구니·찜은 회원 전용이다. 같은 브라우저의 다음 사용자에게 넘어가지 않도록 비운다. */
function clearShopData() {
  clearCartData()
  clearWishlistData()
}

export function useMemberAuth() {
  const isLoggedIn = computed(() => Boolean(member.value))

  const setMember = (nextMember: MemberSession | null) => {
    member.value = nextMember
    if (nextMember) {
      saveMemberSession(nextMember)
      return
    }
    clearMemberSession()
    clearShopData()
  }

  const initMemberAuth = () => {
    if (initialized.value) {
      return Promise.resolve()
    }
    if (pendingInit) {
      return pendingInit
    }

    initializing.value = true
    pendingInit = (async () => {
      try {
        const session = await fetchMemberSession()
        setMember(session)
      } catch {
        /* 세션이 끝났으면 자동 로그인 표시도 같이 걷어낸다 */
        saveAutoLogin('member', false)
        setMember(null)
      } finally {
        initialized.value = true
        initializing.value = false
        pendingInit = null
      }
    })()

    return pendingInit
  }

  const login = async (userId: string, password: string, rememberMe = false) => {
    const result = await loginMember({ userId, password, rememberMe })
    saveAutoLogin('member', rememberMe)
    setMember(result.member)
    initialized.value = true
    return result.member
  }

  const logout = async () => {
    try {
      await logoutMember()
    } finally {
      saveAutoLogin('member', false)
      setMember(null)
      initialized.value = true
    }
  }

  const withdraw = async (reason = '') => {
    await withdrawMember(reason)
    saveAutoLogin('member', false)
    setMember(null)
    initialized.value = true
  }

  return {
    member,
    isLoggedIn,
    initialized,
    initMemberAuth,
    login,
    logout,
    withdraw,
    setMember,
  }
}
